import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SetNewPasswordStep from './SetNewPasswordStep';

// Self-service "forgot password" — reachable from Login.jsx without being
// signed in (that's the whole point). Proves identity via whichever
// contact channel(s) the account actually has on file: phone OTP (stays
// in this tab, synchronous) or an emailed link (EmailPendingStep-style —
// picked up from whatever device the inbox is checked from, handled by
// ResetPassword.jsx separately). See passwordReset.js (backend) header for
// the full design reasoning.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const RESEND_COOLDOWN_SECONDS = 30;

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

// Matches Login.jsx's own card exactly — same dark, moody glass treatment
// against the same background image, since this page is reached directly
// from Login.jsx and previously looked like a jarringly different (light)
// page by comparison.
const card = {
  background: 'rgba(10, 20, 40, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.45)',
};

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: 'rgba(255, 255, 255, 0.85)',
  marginBottom: '5px',
};

// Secondary/muted and emphasized text sitting directly on the card
// background (not inside an input, which keeps its own light background
// and dark text regardless) — same reasoning and same values as Login.jsx's
// own tokens of the same name.
const textMuted = 'rgba(255, 255, 255, 0.75)';
const textStrong = '#fff';

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #D3D1C7',
  borderRadius: '8px',
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const codeInp = { ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' };

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

const btnSecondary = {
  width: '100%',
  padding: '12px',
  background: 'none',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  color: 'rgba(255, 255, 255, 0.85)',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  marginTop: '10px',
};

export default function ForgotPassword() {
  const navigate = useNavigate();

  // 'username' -> 'choose' -> 'phone-otp' -> 'set-password' -> 'done'
  //                        \-> 'email-sent' (terminal for this tab)
  const [step, setStep] = useState('username');
  const [username, setUsername] = useState('');
  const [channels, setChannels] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function handleUsernameSubmit(e) {
    e.preventDefault();
    if (!username.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong.');
        return;
      }
      setChannels(data);
      // Phone-only: nothing to actually choose between, skip straight to
      // it. Email-only or both: land on 'choose' — even with just email
      // available, this makes the "send link to j***@..." action explicit
      // rather than silently firing an email on their behalf.
      if (data.hasPhone && !data.hasEmail) {
        startPhoneOtp();
      } else {
        setStep('choose');
      }
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  async function startPhoneOtp() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send verification code.');
        return;
      }
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStep('phone-otp');
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not resend verification code.');
        return;
      }
      setCode('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setResending(false);
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    setVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      setResetToken(data.resetToken);
      setStep('set-password');
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setVerifying(false);
    }
  }

  async function handleSendLink() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/password-reset/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send reset email.');
        return;
      }
      setStep('email-sent');
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  const titles = {
    username: 'Forgot your password?',
    choose: 'Verify your identity',
    'phone-otp': 'Enter verification code',
    'email-sent': 'Check your inbox',
    'set-password': 'Choose a new password',
    done: 'Password reset',
  };

  return (
    <div style={page}>
      <div style={card}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
            fontSize: '13px', cursor: 'pointer', padding: '8px 16px',
            borderRadius: '4px', marginBottom: '16px', display: 'flex',
            alignItems: 'center', gap: '6px',
          }}
        >
          ← Home
        </button>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: textStrong, margin: '0 0 8px' }}>
            {titles[step]}
          </h1>
        </div>

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {step === 'username' && (
          <form onSubmit={handleUsernameSubmit}>
            <label style={lbl}>Username *</label>
            <input
              style={inp}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              required
            />
            <button type="submit" disabled={loading || !username.trim()} style={(loading || !username.trim()) ? btnDisabled : btn}>
              {loading ? 'Please wait…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'choose' && channels && (
          <div>
            <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px', marginBottom: '20px' }}>
              How would you like to verify it's you?
            </p>
            {channels.hasPhone && (
              <button onClick={startPhoneOtp} disabled={loading} style={loading ? btnDisabled : btn}>
                {loading ? 'Please wait…' : `Send code to phone ending in ${channels.maskedPhone?.slice(-4)}`}
              </button>
            )}
            {channels.hasEmail && (
              <button onClick={handleSendLink} disabled={loading} style={loading ? btnDisabled : btn}>
                {loading ? 'Please wait…' : `Send link to ${channels.maskedEmail}`}
              </button>
            )}
          </div>
        )}

        {step === 'phone-otp' && (
          <form onSubmit={handleVerifyOtp}>
            <p style={{ textAlign: 'center', color: textMuted, fontSize: '14px', marginBottom: '20px' }}>
              We've sent a verification code to your phone.
            </p>
            <label style={lbl}>Enter the 6-digit code *</label>
            <input
              style={codeInp}
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              required
            />
            <button type="submit" disabled={verifying || code.length !== 6} style={(verifying || code.length !== 6) ? btnDisabled : btn}>
              {verifying ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || resendCooldown > 0}
              style={{
                ...btnSecondary,
                opacity: (resending || resendCooldown > 0) ? 0.6 : 1,
                cursor: (resending || resendCooldown > 0) ? 'not-allowed' : 'pointer',
              }}
            >
              {resending ? 'Resending…' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </form>
        )}

        {step === 'email-sent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>📧</div>
            <p style={{ color: textStrong, fontWeight: '600', marginBottom: '8px' }}>Check your inbox</p>
            <p style={{ color: textMuted, fontSize: '14px' }}>
              We've sent a password reset link{channels?.maskedEmail ? ` to ${channels.maskedEmail}` : ''}.
              Click it to set a new password.
            </p>
          </div>
        )}

        {step === 'set-password' && resetToken && (
          // SetNewPasswordStep is shared with ResetPassword.jsx and
          // ChangePasswordRequired.jsx too, both still on a light card —
          // its own internal text colors are tuned for that, not this
          // page's new dark one. Rather than touch that shared component
          // (and risk it looking wrong on the other two pages), it's
          // wrapped in its own light inset panel here so it keeps working
          // exactly as designed, just nested one level deeper visually.
          <div style={{ background: 'rgba(255,255,255,0.92)', borderRadius: '12px', padding: '20px' }}>
            <SetNewPasswordStep
              onSubmit={async (password) => {
                const res = await fetch(`${API_BASE}/auth/password-reset/complete`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ resetToken, newPassword: password }),
                });
                const data = await res.json();
                return res.ok ? { ok: true } : { error: data.error };
              }}
              onSuccess={() => setStep('done')}
            />
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
            <p style={{ color: textStrong, fontWeight: '600', marginBottom: '16px' }}>
              Your password has been reset.
            </p>
            <button onClick={() => navigate('/')} style={btn}>
              Go to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
